// BM marketing copy is DRAFT — see ms/raceday.json _draft
import {
  Calendar,
  Clock,
  MapPin,
  Train,
  Accessibility,
  Heart,
  Car,
  type LucideIcon
} from "lucide-react";

/**
 * Programme + transit structural data shared by both race-day layout variants.
 * User-facing text lives in i18next (namespace `raceday`); this file holds only
 * structure — icons, ids, phases, clock times and the map embed URL.
 */

/**
 * Phases used to group the programme timeline. Items in the same phase
 * render under the same sub-heading; items in different phases get a
 * visual divider between them.
 */
export type ProgrammePhase = "race" | "dinner" | "end";

export interface ProgrammeItem {
  /** Stable key into `raceday:programme.items.<key>`. */
  key: string;
  /** Clock time — data, kept verbatim across languages. */
  time: string;
  /** When true, the item renders its `bullets` array from i18n instead of a single title. */
  hasBullets?: boolean;
  phase: ProgrammePhase;
}

export const programme: ProgrammeItem[] = [
  { key: "registration", time: "3:00 PM", phase: "race" },
  { key: "warmUp", time: "6:45 PM", phase: "race" },
  { key: "ceoPhoto", time: "7:30 PM", phase: "race" },
  { key: "ceoFlagOff", time: "7:40 PM", phase: "race" },
  { key: "mainFlagOff", time: "8:00 PM", phase: "race" },
  { key: "tabulation", time: "8:30 PM", phase: "race" },
  { key: "dinner", time: "9:00 PM", hasBullets: true, phase: "dinner" },
  { key: "endOfEvent", time: "10:00 PM", phase: "end" }
];

/**
 * Accessible public-transport routes to the venue. The source guideline
 * lists MRT + LRT only — no bus or parking. All listed stations are
 * lift-accessible and PWD-suitable.
 */
export interface AccessibleTransitLine {
  /** Stable key into `raceday:gettingThere.transit.<key>`. */
  key: string;
  icon: LucideIcon;
}

export const accessibleTransit: AccessibleTransitLine[] = [
  { key: "mrt", icon: Train },
  { key: "lrt", icon: Train }
];

export interface MeetUpDetail {
  /** Stable key into `raceday:meetUp.<key>`. */
  key: string;
  icon: LucideIcon;
}

export const meetUpDetails: MeetUpDetail[] = [
  { key: "address", icon: MapPin },
  { key: "date", icon: Calendar },
  { key: "time", icon: Clock }
];

// Google Maps "Embed a map" src for the venue (Bursa Malaysia – Stock Exchange
// Centre). Stored here so updating the pin is a data edit, not a code change.
export const venueMapEmbedUrl =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.796580670013!2d101.69871347548148!3d3.1483042531567906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc49d47717c56b%3A0xf6efaf6d9227773a!2sBursa%20Malaysia%20-%20Stock%20Exchange%20Centre!5e0!3m2!1sen!2smy!4v1781504504617!5m2!1sen!2smy";

export interface VillageNote {
  /** Stable key into `raceday:raceVillage.notes.<key>`. */
  key: string;
  icon: LucideIcon;
}

export const villageNotes: VillageNote[] = [
  { key: "emergency", icon: Heart },
  { key: "pwd", icon: Accessibility },
  { key: "parking", icon: Car }
];
